import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { Selector, State } from "./types";
import { createSelector } from "./selector";

// The tags of the members of one project, for the resource tags filter of the project pages. Kept between two
// openings, marked as dirty by the changes of the members or of their tags.
const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  projectUuid: "",
  tags: [],
  isDirty: false,
};

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectResourcesTags"]["request"],
): State {
  return {
    ...state,
    request: setRequestStateToPending(),
    projectUuid: project.uuid,
    tags: project.uuid === state.projectUuid ? state.tags : [],
  };
}

function handleSuccess(
  state: State,
  { project, tags }: Boundaries["retrieveProjectResourcesTags"]["success"],
): State {
  // The tags of a project left meanwhile.
  if (project.uuid !== state.projectUuid) return state;

  return {
    ...state,
    request: setRequestStateToSuccess(),
    tags,
    isDirty: false,
  };
}

function handleFailure(
  state: State,
  { project }: Boundaries["retrieveProjectResourcesTags"]["failure"],
): State {
  if (project.uuid !== state.projectUuid) return state;

  return { ...state, request: setRequestStateToFailure(), isDirty: false };
}

function markAsDirty(state: State): State {
  return { ...state, isDirty: true };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectResourcesTagsRequested]: handleRequest,
    [events.retrieveProjectResourcesTagsSucceeded]: handleSuccess,
    [events.retrieveProjectResourcesTagsFailed]: handleFailure,
    [events.addProjectMaterialResourceSucceeded]: markAsDirty,
    [events.addProjectVirtualResourceSucceeded]: markAsDirty,
    [events.addPlanzoneResourceToProjectSucceeded]: markAsDirty,
    [events.addPlanzoneResourcesToProjectSucceeded]: markAsDirty,
    [events.addEveryoneToProjectSucceeded]: markAsDirty,
    [events.addPlanzoneResourceToProjectsSucceeded]: markAsDirty,
    [events.removeResourceFromProjectSucceeded]: markAsDirty,
    [events.removeResourceFromPlanzoneSucceeded]: markAsDirty,
    [events.editResourceSucceeded]: markAsDirty,
    [events.assignTagSucceeded]: markAsDirty,
    [events.removeTagSucceeded]: markAsDirty,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
